<template>
    <modal-dialog
        ref="modalDialog"
        :confirmBtnTxt="type != 'Delete' ? 'Save' : 'Delete'"
        :isLoading="isLoading"
        @confirm-dialog="save()"
        @close="clear"
    >
        <template v-slot:title>
            {{ type == "Create" ? "New" : type }} Tax
        </template>
        <template v-slot:body>
            <div v-if="type != 'Delete'">
                <b-alert variant="warning" :show="showAlert">
                    <i class="cus-error"></i>
                    Inventories with these inventory categories will be updated
                    to either taxed or not taxed
                </b-alert>
                <div class="form-group row">
                    <label for="name" class="col-sm-3 col-form-label"
                        >Tax:</label
                    >
                    <div class="col-sm-9">
                        <input
                            id="name"
                            v-model="tax.name"
                            name="name"
                            class="form-control"
                            type="text"
                            placeholder="Tax Name (required)"
                            v-validate="'required'"
                            :class="{ 'border-danger': errors.has('name') }"
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label for="rate" class="col-sm-3 col-form-label"
                        >Rate (%):</label
                    >
                    <div class="col-sm-9">
                        <input
                            id="rate"
                            v-model="tax.value"
                            name="rate"
                            class="form-control"
                            type="number"
                            placeholder="Tax Rate (required)"
                            v-validate="'required|decimal:2'"
                            :class="{ 'border-danger': errors.has('rate') }"
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label for="type" class="col-sm-3 col-form-label"
                        >Type:</label
                    >
                    <div class="col-sm-9">
                        <select
                            v-model="tax.type"
                            v-validate="'required'"
                            :class="{ 'border-danger': errors.has('type') }"
                            id="type"
                            name="type"
                            class="form-control"
                            @change="clearJurisdiction"
                        >
                            <option
                                v-for="item in types"
                                :value="item.value"
                                :key="item.value"
                                >{{ item.label }}</option
                            >
                        </select>
                    </div>
                </div>
                <div class="form-group row" ref="jurisdictionWrapper">
                    <label for="jurisdiction" class="col-sm-3 col-form-label"
                        >Jurisdiction:</label
                    >
                    <div class="col-sm-9">
                        <div class="input-group">
                            <input
                                id="jurisdiction"
                                v-model="tax.jurisdiction"
                                name="jurisdiction"
                                class="form-control"
                                type="text"
                                placeholder="Jurisdiction (required)"
                                @input="debouncedSearch"
                                v-validate="'required|min:3'"
                                :class="{
                                    'border-danger': errors.has('jurisdiction')
                                }"
                            />
                            <spinner
                                class="spinner"
                                v-if="isLoadingSearch"
                            ></spinner>
                        </div>
                        <ul
                            v-if="filteredLocations.length > 0"
                            class="list-group dropdown-menu show"
                            aria-labelledby="jurisdiction"
                            style="
                                position: absolute;
                                width: 100%;
                                z-index: 1050;
                            "
                        >
                            <li
                                v-for="jurisdiction in filteredLocations"
                                :key="jurisdiction.id"
                                @click="selectLocations(jurisdiction)"
                                class="list-group-item cursor-pointer"
                            >
                                {{ jurisdiction.text }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <label for="appliesTo" class="col-sm-3 col-form-label"
                        >Applies To:</label
                    >
                    <div class="col-sm-9">
                        <select
                            v-model="tax.all_category"
                            v-validate="'required'"
                            :class="{
                                'border-danger': errors.has('appliesTo')
                            }"
                            id="appliesTo"
                            name="appliesTo"
                            class="form-control"
                        >
                            <option
                                v-for="item in appliesTo"
                                :value="item.value"
                                :key="item.value"
                                >{{ item.label }}</option
                            >
                        </select>
                    </div>
                </div>
                <div class="form-group row" v-if="tax.all_category == 0">
                    <label for="categories" class="col-sm-3 col-form-label"
                        >Categories:</label
                    >
                    <div class="col-sm-9">
                        <dropdown-categories
                            v-if="tax.all_category === 0"
                            @add-category="addCategory"
                            @remove-category="removeCategory"
                            :original-selected="
                                tax && tax.type_category_ids
                                    ? tax.type_category_ids
                                    : []
                            "
                            id="categories"
                            name="categories"
                        />
                    </div>
                </div>
                <div class="form-group row" v-if="type == 'Update'">
                    <label
                        for="overwrite"
                        class="col-sm-3 col-form-label"
                    ></label>
                    <div class="col-sm-9">
                        <label class="checkbox">
                            <input
                                type="checkbox"
                                name="overwrite"
                                v-model="tax.overwrite"
                            />
                            <small>
                                Overwrite the tax application to all selected
                                categories.
                            </small>
                        </label>
                    </div>
                </div>
            </div>
            <div v-else>
                Are you sure you want to permanently delete this tax:
                <b>{{ tax.name }}</b> ?
            </div>
        </template>
    </modal-dialog>
</template>
<script>
import taxService from "@/services/setting/tax";
import ModalDialog from "@/components/commons/modal-dialog";
import DropdownCategories from "@/components/commons/dropdown-categories";
import { debounce } from "lodash";
import Spinner from "@/components/elements/spinner.vue";

export default {
    components: {
        ModalDialog,
        Spinner,
        DropdownCategories
    },
    data() {
        return {
            isLoading: false,
            tax: {
                type: 1,
                all_category: 1,
                overwrite: false,
                default_all_category: 1
            },
            type: "Create",
            filteredLocations: [],
            filteredCategories: [],
            isLoadingSearch: false,
            types: [
                {
                    value: 1,
                    label: "Country"
                },
                {
                    value: 2,
                    label: "State"
                },
                {
                    value: 3,
                    label: "County"
                },
                {
                    value: 4,
                    label: "City"
                }
            ],
            appliesTo: [
                {
                    value: 1,
                    label: "All Inventory Categories"
                },
                {
                    value: 0,
                    label: "Specific Inventory Categories"
                }
            ],
            searchedJurisdiction: false,
            selectedJurisdiction: false
        };
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },

    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    computed: {
        showAlert() {
            return (
                this.type == "Update" &&
                this.tax.all_category == 0 &&
                (this.tax.overwrite ||
                    this.tax.default_all_category != this.tax.all_category)
            );
        }
    },
    methods: {
        async open(type, data) {
            if (data) {
                let taxDetails = JSON.parse(JSON.stringify(data));
                this.tax.id = taxDetails.id;
                this.tax.name = taxDetails.name;
                this.tax.value = taxDetails.value;
                this.tax.type = taxDetails.type;
                this.tax.locations = taxDetails.locations;
                this.tax.jurisdiction = taxDetails.jurisdiction;
                this.tax.all_category = taxDetails.all_category;
                this.tax.default_all_category = taxDetails.all_category;
                //if specific
                if (taxDetails.all_category == 0) {
                    this.tax.type_category_ids = taxDetails.type_category_ids.map(
                        id => ({ id, category_id: id })
                    );
                    this.tax.default_type_category_ids = this.tax.type_category_ids;
                } else {
                    this.tax.type_category_ids = [];
                    this.tax.default_type_category_ids = [];
                }
            }
            this.type = type;
            this.$refs.modalDialog.open();
        },
        close() {
            this.clear();
            this.$refs.modalDialog.close();
        },
        async save() {
            if (this.type != "Delete") {
                let valid = await this.$validator.validateAll();
                if (!valid) {
                    return;
                }
                if (
                    this.tax.all_category == 0 &&
                    this.tax.type_category_ids.length <= 0
                ) {
                    this._message(
                        "Please select at least one (1) category to proceed.",
                        "danger"
                    );
                    return;
                }
            }

            this.isLoading = true;
            try {
                const configTax = await (this.type == "Create"
                    ? this.saveTax()
                    : this.type == "Update"
                    ? this.updateTax()
                    : this.deleteTax());

                const { code, message } = configTax;
                if (code == 200 && message) {
                    this._message(message);
                    this.$emit("reload");
                } else {
                    this._message(
                        `Error: Failed to ${this.type} Tax`,
                        "danger"
                    );
                }
            } catch (error) {
                this.isLoading = false;
                this._message(`Error : ${error.message}`, "danger");
            }

            this.close();
        },
        async saveTax() {
            let params = {
                name: this.tax.name,
                value: this.tax.value,
                type: this.tax.type,
                locations: this.tax.locations,
                apply: this.tax.all_category == 1 ? 0 : 1
            };
            if (params.apply == 1) {
                params.categories = this.tax.type_category_ids.map(
                    obj => obj.id
                );
            }

            const saveTax = await taxService.saveTax(params);
            if (!saveTax) {
                return { code: 404 };
            }
            const { code } = saveTax.message;
            return { code, message: "Tax Created" };
        },
        async updateTax() {
            let params = {
                name: this.tax.name,
                value: this.tax.value,
                type: this.tax.type,
                locations: this.tax.locations,
                apply: this.tax.all_category == 1 ? 0 : 1
            };
            if (params.apply == 1) {
                params.categories = this.tax.type_category_ids.map(
                    obj => obj.id
                );
            }

            if (this.tax.overwrite) {
                params.overwrite = "On";
            }
            const updateTax = await taxService.updateTax(this.tax.id, params);
            if (!updateTax) {
                return { code: 404 };
            }
            const { code } = updateTax.message;
            return { code, message: "Tax Updated" };
        },
        async deleteTax() {
            const deleteTax = await taxService.deleteTax(this.tax.id);
            if (!deleteTax) {
                return { code: 404 };
            }
            const { code, message } = deleteTax.data.response;
            return { code, message };
        },
        clear() {
            this.type = "Create";
            this.isLoading = false;
            this.$validator.reset();
            this.tax = {
                type: 1,
                all_category: 1,
                overwrite: false,
                default_all_category: 1
            };
        },
        async searchLocations() {
            if (
                this.tax.jurisdiction.trim() === "" ||
                this.tax.jurisdiction.trim().length < 3
            ) {
                this.filteredLocations = [];
                return;
            }
            this.isLoadingSearch = true;
            this.isLoading = true;

            const res = await taxService.filterLocations({
                search: this.tax.jurisdiction.trim(),
                type: this.tax.type
            });
            const { resources, message } = res;
            if (message.code == 200) {
                this.filteredLocations = resources;
            }
            this.isLoadingSearch = false;
            this.isLoading = false;
        },
        selectLocations(location) {
            this.tax.locations = location.id;
            this.tax.jurisdiction = location.text;
            this.filteredLocations = [];
            this.selectedJurisdiction = true;
            this.searchedJurisdiction = false;
        },
        debouncedSearch: debounce(function () {
            this.searchLocations();
            this.selectedJurisdiction = false;
            this.searchedJurisdiction = true;
        }, 500),
        addCategory(item) {
            if (!this.tax.type_category_ids) {
                this.tax.type_category_ids = []; // Initialize if undefined
            }
            let catIndex = this.tax.type_category_ids.findIndex(
                cat => cat.id === item.id
            );

            if (catIndex > -1) {
                this.tax.type_category_ids[catIndex].name = item.label;
            } else {
                this.tax.type_category_ids.push({
                    name: item.label,
                    category_id: item.id,
                    id: item.id
                });
            }
        },
        removeCategory(item) {
            this.tax.type_category_ids = this.tax.type_category_ids.filter(
                rec => rec.id !== item.id
            );
        },
        handleClickOutside(event) {
            if (
                this.$refs.jurisdictionWrapper &&
                !this.$refs.jurisdictionWrapper.contains(event.target) &&
                this.searchedJurisdiction
            ) {
                if (!this.selectedJurisdiction) {
                    this.clearJurisdiction();
                }
                this.filteredLocations = [];
                this.searchedJurisdiction = false;
            }
        },
        clearJurisdiction() {
            this.tax.jurisdiction = "";
            this.tax.locations = "";
        }
    }
};
</script>

<style scoped>
.input-group .spinner {
    position: absolute;
    right: 10px;
    z-index: 10;
    top: 50%;
    cursor: pointer;
    -ms-transform: translateY(-50%);
    transform: translateY(-50%);
}
</style>
